/**
 * trace/playAnimation block that delegates playback to the Trace
 * Animation behavior when available.
 *
 * Delegating to animBehavior.play() ensures correct behavior for:
 * - Stopping the previously active clip before starting a new one
 * - Resetting transforms when switching between clips targeting different nodes
 * - Abort controller lifecycle and cleanup
 *
 * Data inputs (`speed`, `loop`) use the same socket names as the KHR spec's
 * animation/start for directional compatibility.
 *
 * When no Animation behavior is present (e.g. raw KHR_interactivity on a glTF),
 * falls back to direct AnimationGroup.start() on the SceneObject's clips.
 */

import { FlowGraphAsyncExecutionBlock } from "@babylonjs/core/FlowGraph/flowGraphAsyncExecutionBlock";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import { RichTypeNumber, RichTypeBoolean } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { Animation } from '../../animation';
import { Logger } from '../../../utils/Logger';
import { resolveTarget } from './traceContext';

export class TracePlayAnimationBlock extends FlowGraphAsyncExecutionBlock {
    private _targetPath: string;
    private _clip: string | undefined;
    private _generation = 0;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string; clip?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this._clip = config?.clip;
        this.registerDataInput("speed", RichTypeNumber, 1);
        this.registerDataInput("loop", RichTypeBoolean, false);
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const speed = this.getDataInput("speed")!.getValue(context) as number;
        const loop = this.getDataInput("loop")!.getValue(context) as boolean;
        const target = resolveTarget(context, this._targetPath);

        if (!target) {
            this.out._activateSignal(context);
            return;
        }

        const animBehavior = target.getBehavior(Animation);
        if (animBehavior) {
            this._executeViaBehavior(context, animBehavior, speed, loop);
        } else {
            this._executeDirectly(context, target, speed, loop);
        }
    }

    private _executeViaBehavior(
        context: FlowGraphContext,
        animBehavior: Animation,
        speed: number,
        loop: boolean,
    ): void {
        const gen = ++this._generation;

        const playPromise = animBehavior.play({
            clip: this._clip,
            speed,
            loop,
        });

        this.out._activateSignal(context);

        if (!loop) {
            void playPromise.then(() => {
                if (this._generation === gen) {
                    this.done._activateSignal(context);
                }
            });
        }
    }

    private _executeDirectly(
        context: FlowGraphContext,
        target: { name: string; animationClips: ReadonlyMap<string, AnimationGroup> },
        speed: number,
        loop: boolean,
    ): void {
        this._removePendingObserver(context);

        const group = this._resolveAnimationGroup(target.animationClips);
        if (!group) {
            Logger.warn(`[TracePlayAnimationBlock] No animation "${this._clip ?? '(default)'}" on ${target.name}`);
            this.out._activateSignal(context);
            return;
        }

        const gen = ++this._generation;

        if (group.isPlaying) group.stop();
        group.start(loop, speed);

        this.out._activateSignal(context);

        if (!loop) {
            const onEnd = group.onAnimationGroupEndObservable.addOnce(() => {
                if (this._generation === gen) {
                    this.done._activateSignal(context);
                }
            });
            context._setExecutionVariable(this, "_pendingObserver", onEnd);
            context._setExecutionVariable(this, "_pendingGroup", group);
        }
    }

    private _removePendingObserver(context: FlowGraphContext): void {
        const onEnd = context._getExecutionVariable(this, "_pendingObserver", null) as Observer<AnimationGroup> | null;
        const group = context._getExecutionVariable(this, "_pendingGroup", null) as AnimationGroup | null;
        if (onEnd && group) {
            group.onAnimationGroupEndObservable.remove(onEnd);
        }
        context._deleteExecutionVariable(this, "_pendingObserver");
        context._deleteExecutionVariable(this, "_pendingGroup");
    }

    private _resolveAnimationGroup(clips: ReadonlyMap<string, AnimationGroup>): AnimationGroup | null {
        if (this._clip) return clips.get(this._clip) ?? null;
        return clips.values().next().value ?? null;
    }

    public override _preparePendingTasks(_context: FlowGraphContext): void {
        // No-op
    }

    public override _cancelPendingTasks(context: FlowGraphContext): void {
        this._generation++;
        this._removePendingObserver(context);
    }

    public override getClassName(): string {
        return "TracePlayAnimationBlock";
    }
}
